import React from 'react';
import { Link } from 'react-router-dom';

const Process = () => {
  const phases = [
    {
      number: '01',
      title: 'Discovery',
      description: 'We begin by listening. Understanding your vision, your standards, how you want your space to perform for decades, not just years.',
      details: [
        'Site visit and comprehensive analysis',
        'Detailed brief and aspirations mapping',
        'Understanding of scope and long-term goals',
        'For architects: studying every drawing to ensure buildability'
      ],
      duration: 'Typically 1-2 weeks'
    },
    {
      number: '02',
      title: 'Concept & Design',
      description: 'Our design team translates your vision into a spatial narrative — exploring materials, structure, proportion, and engineering until every detail feels inevitable.',
      details: [
        'Concept presentations with 3D visualizations',
        'Material palette exploration and specification',
        'Structural feasibility and engineering integration',
        'Design refinement with your feedback',
        'For architects: build intelligence added without compromising vision'
      ],
      duration: '3-6 weeks depending on scope'
    },
    {
      number: '03',
      title: 'Planning & Approvals',
      description: 'We handle the complexity so you do not have to - from structural engineering to municipal approvals, every technical detail is managed in-house.',
      details: [
        'Technical drawings and construction documentation',
        'Structural planning and MEP coordination',
        'Regulatory approvals and compliance',
        'Contractor coordination and scheduling'
      ],
      duration: '2-4 weeks'
    },
    {
      number: '04',
      title: 'Build & Craft',
      description: 'Construction begins with precision. Our project leads are on-site daily, ensuring every element meets the standard our clients — and their architects — expect.',
      details: [
        'Dedicated project manager assigned',
        'Weekly progress updates with photo documentation',
        'Quality checkpoints at every milestone',
        'On-site supervision and quality control',
        'Real-time issue resolution'
      ],
      duration: 'Timeline shared at project start'
    },
    {
      number: '05',
      title: 'Handover & Beyond',
      description: 'We do not disappear after the keys are handed over. A final walkthrough, detailed handover, and continued support - because what we build is meant to last.',
      details: [
        'Comprehensive quality inspection',
        'Detailed handover documentation',
        'Maintenance guidelines and warranty',
        '1-year structural support commitment',
        'Ongoing relationship and support'
      ],
      duration: 'Lifetime commitment'
    }
  ];

  return (
    <div className="bg-[#f5f2ed] pt-24">
      {/* Hero */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-['Playfair_Display'] text-[#1a1a1a] mb-8 fade-in">
            How We Work
          </h1>
          <p className="text-xl md:text-2xl text-[#9c9590] leading-relaxed fade-in" style={{ animationDelay: '0.2s' }}>
            From first meeting to final walkthrough — a seamless experience built around precision and trust.
          </p>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="section-padding bg-white">
        <div className="max-w-[1200px] mx-auto">
          {phases.map((phase, index) => (
            <div
              key={index}
              className="mb-24 last:mb-0 fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                {/* Phase Number */}
                <div className="md:col-span-2">
                  <span className="text-6xl md:text-7xl font-['Playfair_Display'] text-[#b8986e]/30">
                    {phase.number}
                  </span>
                </div>

                {/* Phase Content */}
                <div className="md:col-span-10">
                  <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] text-[#1a1a1a] mb-4">
                    {phase.title}
                  </h2>
                  <p className="text-lg text-[#1a1a1a] leading-relaxed mb-6">
                    {phase.description}
                  </p>

                  {/* What Happens */}
                  <div className="mb-6">
                    <h3 className="text-sm tracking-widest uppercase text-[#b8986e] mb-3">
                      What happens
                    </h3>
                    <ul className="space-y-2">
                      {phase.details.map((detail, idx) => (
                        <li key={idx} className="text-[#9c9590] flex items-start">
                          <span className="inline-block w-1.5 h-1.5 bg-[#b8986e] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Duration */}
                  <p className="text-sm text-[#9c9590] italic">
                    {phase.duration}
                  </p>
                </div>
              </div>

              {/* Separator Line */}
              {index < phases.length - 1 && (
                <div className="mt-16 border-b border-[#9c9590]/20"></div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Background Image Section */}
      <section className="relative h-[500px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1721244653721-bc681b2dfd27"
          alt="Architectural Process"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-[#1a1a1a]/40"></div>
      </section>

      {/* Closing CTA */}
      <section className="section-padding text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] text-[#1a1a1a] mb-8">
            Ready to begin?
            <br />
            Every project starts with a simple conversation.
          </h2>
          <Link
            to="/contact"
            className="inline-block bg-[#1a1a1a] text-[#f5f2ed] px-12 py-4 text-base tracking-wide hover:bg-[#2a2a2a] transition-all duration-300"
          >
            Book a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Process;
